import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { createFeedbackBoardThread, getFeedbackBoardThreads } from '../services/api';
import { flattenMenuManifest } from '../config/menuManifest';
import { findMenuItemForPath } from '../utils/pagePermissions';
import {
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  WORK_TYPE_OPTIONS,
  getApprovalRequestLabel,
  getDefaultWorkTypeForCategory,
  isApprovalRequestCategory,
} from '../features/feedbackBoard/constants';

const EMPTY_DRAFT = Object.freeze({
  title: '',
  body: '',
  category: 'improvement',
  workType: 'development',
  priority: 'normal',
  requestedQuantity: '',
  deliveryAddress: '',
  testMode: false,
});

const FeedbackBoardLauncher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const menuItems = useMemo(() => flattenMenuManifest(), []);
  const activeMenuItem = useMemo(
    () => findMenuItemForPath(location.pathname, menuItems),
    [location.pathname, menuItems]
  );

  const pageContext = useMemo(() => {
    const path = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
    const href = typeof window !== 'undefined'
      ? `${window.location.origin}${path}`
      : path;
    const fallbackTitle = typeof document !== 'undefined' ? document.title : 'Workspace';
    const title = activeMenuItem?.label || fallbackTitle || 'Workspace';
    return { title, path, href };
  }, [activeMenuItem?.label, location.hash, location.pathname, location.search]);

  useEffect(() => {
    let isActive = true;

    const syncUnreadCount = async () => {
      try {
        const response = await getFeedbackBoardThreads();
        if (!isActive) return;
        const nextCount = Number(response?.summary?.unreadForCurrentUser || 0);
        setUnreadCount(Number.isFinite(nextCount) ? nextCount : 0);
      } catch (loadError) {
        if (isActive) {
          console.error('Error fetching Quick Feedback unread count:', loadError);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncUnreadCount();
      }
    };

    const handleSummarySync = (event) => {
      if (!isActive) return;
      const nextCount = Number(event?.detail?.unreadForCurrentUser || 0);
      setUnreadCount(Number.isFinite(nextCount) ? nextCount : 0);
    };

    syncUnreadCount();
    window.addEventListener('focus', syncUnreadCount);
    window.addEventListener('feedback-board:summary', handleSummarySync);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActive = false;
      window.removeEventListener('focus', syncUnreadCount);
      window.removeEventListener('feedback-board:summary', handleSummarySync);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.hash, location.pathname, location.search]);

  const resetForm = () => {
    setDraft({ ...EMPTY_DRAFT });
    setImageFile(null);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('title', draft.title);
      payload.append('body', draft.body);
      payload.append('category', draft.category);
      payload.append('workType', draft.workType || getDefaultWorkTypeForCategory(draft.category));
      payload.append('priority', draft.priority);
      if (isApprovalRequestCategory(draft.category)) {
        payload.append('requestedQuantity', draft.requestedQuantity);
        payload.append('deliveryAddress', draft.deliveryAddress);
        payload.append('testMode', draft.testMode ? 'true' : 'false');
      }
      payload.append('pageTitle', pageContext.title);
      payload.append('pagePath', pageContext.path);
      payload.append('pageHref', pageContext.href);
      if (imageFile) {
        payload.append('image', imageFile);
      }

      await createFeedbackBoardThread(payload);
      try {
        const response = await getFeedbackBoardThreads();
        const nextSummary = response?.summary || null;
        const nextCount = Number(nextSummary?.unreadForCurrentUser || 0);
        setUnreadCount(Number.isFinite(nextCount) ? nextCount : 0);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('feedback-board:summary', { detail: nextSummary }));
        }
      } catch (syncError) {
        console.error('Error refreshing Quick Feedback unread count:', syncError);
      }
      resetForm();
      setOpen(false);
      setSuccessOpen(true);
    } catch (submitError) {
      setError(
        submitError?.response?.data?.error ||
        submitError?.response?.data?.message ||
        submitError?.message ||
        'Failed to save feedback item.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUpdates = () => {
    navigate(`/feedback-board?view=updates&focus=${Date.now()}`);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <IconButton
          size="small"
          className="platform-header__feedback-trigger"
          onClick={handleOpenUpdates}
          aria-label={unreadCount > 0 ? `Feedback updates, ${unreadCount} unread` : 'Feedback updates'}
          title={unreadCount > 0 ? `Feedback updates • ${unreadCount} unread` : 'Feedback updates'}
        >
          <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular" invisible={unreadCount < 1}>
            <CampaignOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>

        <IconButton
          size="small"
          className="platform-header__feedback-trigger"
          onClick={() => setOpen(true)}
          aria-label="Add feedback"
          title="Add feedback"
        >
          <AddOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '18px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Quick Feedback</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '10px !important' }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(15, 118, 198, 0.05), rgba(30, 64, 175, 0.08))',
            }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              Source Page
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {pageContext.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
              {pageContext.path}
            </Typography>
          </Box>

          <TextField
            label="Title"
            value={draft.title}
            onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
            fullWidth
          />

          <TextField
            select
            label="Category"
            value={draft.category}
            onChange={(event) => setDraft((current) => {
              const nextCategory = event.target.value;
              const currentDefaultWorkType = getDefaultWorkTypeForCategory(current.category);
              const nextDefaultWorkType = getDefaultWorkTypeForCategory(nextCategory);
              return {
                ...current,
                category: nextCategory,
                workType: current.workType === currentDefaultWorkType ? nextDefaultWorkType : current.workType,
              };
            })}
            fullWidth
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Work type"
            value={draft.workType}
            onChange={(event) => setDraft((current) => ({ ...current, workType: event.target.value }))}
            fullWidth
          >
            {WORK_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priority"
            value={draft.priority}
            onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))}
            fullWidth
          >
            {PRIORITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {isApprovalRequestCategory(draft.category) ? (
            <>
              <Alert severity={draft.testMode ? 'info' : 'warning'}>
                {draft.testMode
                  ? 'Test mode is active. Approval and participant emails are suppressed for this request.'
                  : `${getApprovalRequestLabel(draft.category)}s start in Waiting for Approval and notify the approval audience.`}
              </Alert>
              <TextField
                label="Requested quantity"
                type="number"
                value={draft.requestedQuantity}
                onChange={(event) => setDraft((current) => ({ ...current, requestedQuantity: event.target.value }))}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
                helperText={`Required for ${getApprovalRequestLabel(draft.category)}s.`}
              />
              <TextField
                label="Delivery address"
                value={draft.deliveryAddress}
                onChange={(event) => setDraft((current) => ({ ...current, deliveryAddress: event.target.value }))}
                fullWidth
                multiline
                minRows={2}
                helperText="Optional. You can still edit it later from the board."
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={Boolean(draft.testMode)}
                    onChange={(event) => setDraft((current) => ({ ...current, testMode: event.target.checked }))}
                  />
                )}
                label="Test mode: suppress emails"
              />
            </>
          ) : null}

          <TextField
            label="Details"
            value={draft.body}
            onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))}
            fullWidth
            multiline
            minRows={5}
            helperText="The current page link will be attached automatically."
          />

          {draft.category === 'knowledge_base' ? (
            <Alert severity="info">
              Use <strong>Knowledge Base</strong> for support questions, documentation gaps or requests for page guidance.
              If the topic turns into implementation work later, it can still be moved to `Improvement` or `Issue`.
            </Alert>
          ) : null}

          <Stack spacing={1}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachmentOutlinedIcon />}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {imageFile ? 'Change image' : 'Attach image'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => setImageFile(event.target.files?.[0] || null)}
              />
            </Button>
            {imageFile ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Attached: {imageFile.name}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button component={RouterLink} to="/feedback-board" startIcon={<OpenInNewOutlinedIcon />} onClick={handleClose}>
            Open board
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={3200}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
          Feedback item added.
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackBoardLauncher;
